import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  ViewChild,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import {
  STATUS_GROUPS,
  StatusGroup,
  StatusGroupId,
  WORKFLOW_STATUS_GROUP,
  WORKFLOW_STATUS_NAMES,
} from '../agenda-calendar/agenda-calendar.models'

interface LegendItem {
  workflowId: number
  name: string
}

interface LegendNode {
  /** Stable key used for tracking + open-state. */
  key: string
  type: 'group' | 'parent'
  group?: StatusGroup
  items?: LegendItem[]
  parentLabel?: string
  childGroups?: { group: StatusGroup; items: LegendItem[] }[]
}

/**
 * Horizontal status legend strip. Each top-level entry renders as a pill (color dot + label).
 * Clicking a pill opens a popover anchored below it with the underlying status names. The
 * "Laudo" parent shows two sub-groups inside its popover, each expandable to its own status list.
 *
 * No interactive filtering yet — Phase 2 will hook the items into the agenda filter.
 */
@Component({
  selector: 'app-status-legend',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './status-legend.component.html',
  styleUrl: './status-legend.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusLegendComponent {
  @ViewChild('hostEl', { static: true }) private hostEl?: ElementRef<HTMLElement>

  protected readonly nodes: LegendNode[] = this.buildNodes()
  protected openKey: string | null = null
  // For the parent "Laudo" popover: which sub-group is currently expanded inside.
  protected openSubKey: string | null = null

  protected toggle(key: string, event: Event): void {
    event.stopPropagation()
    if (this.openKey === key) {
      this.openKey = null
      this.openSubKey = null
      return
    }
    this.openKey = key
    this.openSubKey = null
  }

  protected toggleSub(subKey: string, event: Event): void {
    event.stopPropagation()
    this.openSubKey = this.openSubKey === subKey ? null : subKey
  }

  /** Click anywhere outside the legend → close popover. */
  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.openKey) return
    const target = event.target as Node | null
    if (target && this.hostEl?.nativeElement.contains(target)) return
    this.openKey = null
    this.openSubKey = null
  }

  protected trackByNode(_: number, node: LegendNode): string {
    return node.key
  }

  protected trackByItem(_: number, item: LegendItem): number {
    return item.workflowId
  }

  protected trackBySub(_: number, sub: { group: StatusGroup; items: LegendItem[] }): string {
    return sub.group.id
  }

  // Builds the legend list from the static maps. Order follows the patient/exam journey.
  // The synthetic "Laudo" parent wraps the two sub-groups (reportProcessing + reportDelivery).
  private buildNodes(): LegendNode[] {
    const order: StatusGroupId[] = [
      'scheduling',
      'confirmed',
      'presence',
      'inService',
      'finished',
      'problematic',
      'neutral',
    ]

    const itemsByGroup: Record<StatusGroupId, LegendItem[]> = {
      scheduling: [], confirmed: [], presence: [], inService: [],
      reportProcessing: [], reportDelivery: [],
      finished: [], problematic: [], neutral: [],
    }
    for (const [idStr, gid] of Object.entries(WORKFLOW_STATUS_GROUP)) {
      const id = Number(idStr)
      itemsByGroup[gid].push({ workflowId: id, name: WORKFLOW_STATUS_NAMES[id] ?? `#${id}` })
    }
    for (const arr of Object.values(itemsByGroup)) {
      arr.sort((a, b) => a.name.localeCompare(b.name))
    }

    const nodes: LegendNode[] = []
    for (const gid of order) {
      nodes.push({
        key: gid,
        type: 'group',
        group: STATUS_GROUPS[gid],
        items: itemsByGroup[gid],
      })
      // Insert the "Laudo" parent right after 'inService' to honor the journey order.
      if (gid === 'inService') {
        nodes.push({
          key: 'laudo',
          type: 'parent',
          parentLabel: 'Laudo',
          childGroups: [
            { group: STATUS_GROUPS.reportProcessing, items: itemsByGroup.reportProcessing },
            { group: STATUS_GROUPS.reportDelivery, items: itemsByGroup.reportDelivery },
          ],
        })
      }
    }
    return nodes
  }
}
